/**
 * Sync port of the saga generator that decided where a freshly-added widget
 * should land in the dashboard. Used by `WorkspaceService.addWidget` so the
 * orchestration runs as plain async/await.
 *
 * Mirrors `store/sagas/widgets/construction/widget-layout.ts` exactly — the
 * decision logic (mosaic placement, cascade fallback, overlap detection) is
 * unchanged. The original saga generator stays for now and is removed when
 * the addWidget saga becomes a shim.
 */

import type { Layout } from '@thinkalpha/ui-types/contracts/workspace';
import type { LegacyWidgetDefinition } from 'src/lib/widget/WidgetDefinition';
import { ceilToMosaicGrid } from 'src/routes/dashboard/svelte-layout/mosaic-drop-targets';
import type { NormalizedLayout } from 'src/routes/dashboard/svelte-layout/mosaic-drop-targets';
import { findOptimalWidgetPlacement } from 'src/routes/dashboard/svelte-layout/mosaic-widget-placement';
import { container as staticContainer } from 'src/StaticContainer';
import type { RootState } from 'src/store';
import { getContainerLayoutViewModels } from 'src/store/selectors/layout';
import { getCurrentWorkspace } from 'src/store/selectors/workspace';

const log = staticContainer.get('Logger').getSubLogger({ name: 'widgetLayout' });

export const CENTER_POSITION: Pick<Layout, 'x' | 'y'> = Object.freeze({
    x: 0.5,
    y: 0.5,
});

/**
 * Dimensions to cascade by if widgets overlap. Also acts as a margin for overlap detection.
 */
export const CASCADE_DIMENSIONS: Pick<Layout, 'x' | 'y'> = Object.freeze({
    x: 0.01,
    y: 0.025,
});

export const DEFAULT_DIMENSIONS: Layout = Object.freeze({
    h: 0.2,
    w: 0.6,
    x: CENTER_POSITION.x,
    y: CENTER_POSITION.y,
});

const MAX_CASCADES = 20;

/**
 * Result of computeWidgetLayout — includes layout and optionally affected layouts for mosaic mode.
 */
export interface WidgetLayoutResult {
    layout: Layout;
    /** When mosaic placement splits/inserts, contains updated layouts for affected widgets */
    affectedLayouts?: Record<string, NormalizedLayout>;
}

/**
 * Checks if two layouts have top-left positions that are too close (within margin).
 */
function layoutsOverlapWithMargin(layout1: Layout, layout2: Layout, margin: Pick<Layout, 'x' | 'y'>): boolean {
    const xDiff = Math.abs(layout1.x - layout2.x);
    const yDiff = Math.abs(layout1.y - layout2.y);

    return xDiff < margin.x && yDiff < margin.y;
}

/**
 * Compute a layout for a freshly-added widget given the current workspace state.
 *
 * When mosaic mode is enabled (workspace's `enableMosaicLayout` setting):
 * 1. Uses `findOptimalWidgetPlacement` to find the best position
 * 2. May return `affectedLayouts` if placement requires splitting/inserting
 *
 * When mosaic mode is disabled (fallback):
 * 1. Centers the widget at screen center (CENTER_POSITION)
 * 2. Checks all existing layouts for overlap within {@link CASCADE_DIMENSIONS}
 * 3. Cascades by CASCADE_DIMENSIONS if there's overlap, repeatedly, until no overlap or {@link MAX_CASCADES} reached
 * 4. Constrains the cascaded widget to the dashboard bounds.
 */
export function computeWidgetLayout(
    widgetDefinition: Pick<LegacyWidgetDefinition, 'defaultDimensions'>,
    state: RootState,
    fallbackDimensions: Layout = DEFAULT_DIMENSIONS,
    preferredDimensions?: { w: number; h: number },
): WidgetLayoutResult {
    const allLayouts = getContainerLayoutViewModels(state) as Record<string, Layout> | null | undefined;
    const existingLayouts = Object.values(allLayouts ?? {}).filter(Boolean);

    const width = preferredDimensions?.w ?? widgetDefinition.defaultDimensions?.w ?? fallbackDimensions.w;
    const height = preferredDimensions?.h ?? widgetDefinition.defaultDimensions?.h ?? fallbackDimensions.h;

    const workspace = getCurrentWorkspace(state);
    const enableMosaicLayout = workspace?.layoutSettings?.enableMosaicLayout ?? false;

    if (TRACE) {
        log.trace({
            message: 'computeWidgetLayout: checking mosaic placement conditions',
            enableMosaicLayout,
            existingWidgetCount: Object.keys(allLayouts ?? {}).length,
            requestedSize: { w: width, h: height },
        });
    }

    if (enableMosaicLayout && allLayouts) {
        const normalizedLayouts: Record<string, NormalizedLayout> = {};
        for (const [id, layout] of Object.entries(allLayouts)) {
            if (layout) {
                normalizedLayouts[id] = { x: layout.x, y: layout.y, w: layout.w, h: layout.h };
            }
        }

        const mosaicWidth = ceilToMosaicGrid(width);
        const mosaicHeight = ceilToMosaicGrid(height);
        const result = findOptimalWidgetPlacement(normalizedLayouts, { w: mosaicWidth, h: mosaicHeight });
        if (TRACE) {
            log.trace({
                message: 'computeWidgetLayout: mosaic placement result',
                placementType: result?.type ?? 'null',
                layout: result?.layout,
                affectedLayoutCount: result?.affectedLayouts ? Object.keys(result.affectedLayouts).length : 0,
            });
        }
        if (result) {
            return {
                layout: result.layout,
                affectedLayouts: result.affectedLayouts,
            };
        }
    }

    if (TRACE) {
        log.trace({
            message: 'computeWidgetLayout: using center+cascade fallback',
            reason: !enableMosaicLayout
                ? 'enableMosaicLayout setting is false (explicitly disabled)'
                : !allLayouts
                  ? 'no existing layouts'
                  : 'mosaic placement returned null',
        });
    }

    const centeredX = CENTER_POSITION.x - width / 2;
    const centeredY = CENTER_POSITION.y - height / 2;

    let proposedLayout: Layout = {
        x: centeredX,
        y: centeredY,
        w: width,
        h: height,
    };

    let cascadeCount = 0;
    let xCascadeCount = 0;
    let yCascadeCount = 0;

    while (
        cascadeCount < MAX_CASCADES &&
        existingLayouts.some((existingLayout) =>
            layoutsOverlapWithMargin(proposedLayout, existingLayout, CASCADE_DIMENSIONS),
        )
    ) {
        cascadeCount++;

        let newX = centeredX + CASCADE_DIMENSIONS.x * (xCascadeCount + 1);
        let newY = centeredY + CASCADE_DIMENSIONS.y * (yCascadeCount + 1);

        const canCascadeX = newX >= 0 && newX + width <= 1;
        const canCascadeY = newY >= 0 && newY + height <= 1;

        if (canCascadeX) {
            xCascadeCount++;
            newX = centeredX + CASCADE_DIMENSIONS.x * xCascadeCount;
        } else {
            newX = Math.max(0, Math.min(centeredX + CASCADE_DIMENSIONS.x * xCascadeCount, 1 - width));
        }

        if (canCascadeY) {
            yCascadeCount++;
            newY = centeredY + CASCADE_DIMENSIONS.y * yCascadeCount;
        } else {
            newY = Math.max(0, Math.min(centeredY + CASCADE_DIMENSIONS.y * yCascadeCount, 1 - height));
        }

        proposedLayout = {
            x: newX,
            y: newY,
            w: width,
            h: height,
        };
    }

    return { layout: proposedLayout };
}
